export const STEAM_LAB_AMOUNTS = [3000, 6000, 15000, 30000, 60000] as const;
export type SteamLabMethod = 'alipay' | 'wechat';
export type SteamLabOrder = {
  orderNo: string; amountFen: number; currency: 'CNY'; method: SteamLabMethod; accountId: string;
  transid?: string | null; status: 'pending' | 'paid' | 'manual_review'; stage: string; qrReady: boolean;
  createdAt: string; updatedAt: string; paidAt?: string; qrReadyAt?: string; qrDurationMs?: number; testEndedAt?: string;
  qrTimingsMs?: { queue: number; identity: number; checkout: number; cashier: number };
  checkoutTransport?: 'http' | 'browser'; qrSource?: 'http_image' | 'browser_pixels';
  failureCode?: string;
  qrIssue?: string | null;
  canRetryPreparation?: boolean;
};
export type SteamLabState = {
  configured: boolean; accountId: string | null;
  loginStatus: 'unknown' | 'awaiting_login' | 'ready' | 'account_mismatch' | 'unavailable';
  loggedInAccountId?: string | null; orders: SteamLabOrder[];
  maxActiveOrders?: number; maxAccounts?: number;
  accounts?: { accountId: string; loginStatus: SteamLabState['loginStatus']; loggedInAccountId: string | null; activeOrders: number }[];
};
export type SteamLabIntent = Pick<SteamLabOrder, 'orderNo' | 'amountFen' | 'method' | 'accountId'>;
export const steamLabOwner = (role: string) => role === 'owner';
export const steamLabReleased = (order: SteamLabOrder) => order.status === 'paid' || Boolean(order.testEndedAt);
export const steamLabCanRetry = (order: SteamLabOrder) => order.canRetryPreparation === true && order.status === 'manual_review' && !order.transid && !steamLabReleased(order);
export const steamLabOrderLabel = (order: SteamLabOrder) => order.status === 'paid' ? '充值成功' : order.testEndedAt ? '已结束测试 · 未确认到账' : order.status === 'manual_review' ? '需要核对' : '待确认';
export function validSteamLabAccount(value: string): boolean {
  if (!/^\d{17}$/.test(value)) return false;
  const account = BigInt(value) - 76561197960265728n;
  return account > 0n && account <= 4294967295n;
}
export function parseSteamLabIntent(raw: string | null): SteamLabIntent | null {
  try {
    const value = JSON.parse(raw ?? 'null') as SteamLabIntent | null;
    if (!value || typeof value.orderNo !== 'string' || !/^lab_[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(value.orderNo)
      || !STEAM_LAB_AMOUNTS.some(amount => amount === value.amountFen) || !['alipay', 'wechat'].includes(value.method)
      || typeof value.accountId !== 'string' || !validSteamLabAccount(value.accountId)) return null;
    return { orderNo: value.orderNo, amountFen: value.amountFen, method: value.method, accountId: value.accountId };
  } catch { return null; }
}
export function mergeSteamLabOrders(previous: SteamLabOrder[], incoming: SteamLabOrder[]): SteamLabOrder[] {
  const orders = new Map(previous.map(order => [order.orderNo, order]));
  for (const order of incoming) {
    const saved = orders.get(order.orderNo);
    if (saved?.status === 'paid' || (saved && Date.parse(saved.updatedAt) > Date.parse(order.updatedAt))) continue;
    if (saved?.testEndedAt && !order.testEndedAt && order.status !== 'paid') continue;
    orders.set(order.orderNo, order);
  }
  return [...orders.values()].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}
export function steamLabPending(orders: SteamLabOrder[], intent: SteamLabIntent | null, capacity = 1, accountId?: string | null) {
  const active = orders.filter(order => !steamLabReleased(order) && (!accountId || order.accountId === accountId));
  const unresolved = active[0];
  const intentOrder = intent ? orders.find(order => order.orderNo === intent.orderNo) : undefined;
  const pendingIntent = intent && !intentOrder ? intent : null;
  return { unresolved, pendingIntent, activeCount: active.length, blocked: active.length >= capacity || Boolean(pendingIntent) };
}
export function steamLabImage(value: unknown): string | null {
  return typeof value === 'string' && value.length <= 16_777_216 && /^data:image\/png;base64,[A-Za-z\d+/]+={0,2}$/.test(value) ? value : null;
}
const qrPreparationStages = new Set(['accepted', 'identity_check', 'wallet_page', 'login_check', 'amount_selection', 'payment_method', 'init_transaction', 'price_validation', 'agreement', 'payment_popup', 'qr_detection', 'http_wallet', 'http_wallet_submit', 'http_checkout_page', 'http_init', 'http_price', 'http_qr', 'browser_qr', 'query', 'resume_query', 'resume_payment_page']);
export function steamLabPollInterval(order: SteamLabOrder | undefined, loginStatus: SteamLabState['loginStatus'] | undefined, now = Date.now()): number {
  if (!order || steamLabReleased(order) || loginStatus !== 'ready' || order.status !== 'pending' || order.qrReady || order.qrReadyAt || !qrPreparationStages.has(order.stage)) return 5000;
  const elapsed = now - Date.parse(order.createdAt);
  return Number.isFinite(elapsed) && elapsed >= 0 && elapsed < 90_000 ? 1000 : 5000;
}
export function steamLabQrDuration(order: SteamLabOrder): string | null {
  const duration = order.qrDurationMs;
  return typeof duration === 'number' && Number.isSafeInteger(duration) && duration >= 0 ? (duration / 1000).toFixed(1) : null;
}
export function steamLabQrImage(order: SteamLabOrder, qr: { orderNo: string; image: string } | null, error: string): string | null {
  return !steamLabReleased(order) && order.qrReady && !error && qr?.orderNo === order.orderNo ? steamLabImage(qr.image) : null;
}
export function steamLabCreateRejected(error: unknown): boolean {
  return error instanceof Error && ['account_mismatch', 'account_not_configured', 'account_operation_in_progress', 'account_has_unresolved_order', 'account_order_capacity', 'invalid_order'].includes(error.message);
}
const errors: Record<string, string> = {
  account_order_capacity: '此账号的待处理订单已达上限，请处理原单或选择其他收款账号。',
  account_pool_capacity: '收款账号数量已达测试配置上限。',
  steam_lab_unavailable: '暂时未能读取 Steam 测试服务，页面会自动重试。已显示的账号和订单为上次读取结果。',
  steam_lab_invalid_response: 'Steam 测试服务返回的数据未通过校验，已保留原订单，请检查服务诊断。',
  lab_disabled: 'Steam 测试服务尚未启用，请完成服务发布后刷新。',
  account_operation_in_progress: '账号操作正在进行，请等待本次操作完成后刷新。',
  account_not_configured: '尚未绑定收款账号，请填写 SteamID64。',
  awaiting_login: '请先用手机 Steam 完成账号登录。', account_mismatch: '登录账号与绑定 SteamID 不一致，请登录绑定账号。',
  stored_account_mismatch: '旧账号仍有未确认订单，请先核清原单再换号。', account_has_unresolved_order: '已有未确认订单，请先查看原单。此次请求未被接受，可稍后重试同一订单号。',
  idempotency_conflict: '原订单参数不一致，请刷新并核对原单，不能改金额后重建。',
  qr_not_ready: '付款码尚未就绪，正在等待付款页加载。', login_image_unavailable: '登录图暂未就绪，请稍后刷新。',
  invalid_account: '请输入有效的 17 位 SteamID64。', invalid_account_configuration: '请输入有效的 17 位 SteamID64。',
  invalid_order: '订单参数无效，请使用页面提供的固定面额。', forbidden: '此功能仅向 owner 开放。',
  admin_access_required: '后台二次验证已失效，请刷新页面重新验证。', unauthorized: '管理员会话已失效，请刷新页面重新登录。',
  order_not_found: '暂未找到此单；如提交结果未知，请使用保留的原订单号重试。',
  invalid_test_end: '请核对当前订单并勾选结束测试确认。', historical_order_read_only: '这是其他收款账号的历史订单；请使用原账号核对到账结果。',
  order_parameters_conflict: '原订单信息已更新，请刷新状态后再操作。',
  preparation_retry_not_allowed: '这笔订单已进入交易流程或状态已变化，不能重试建单，请刷新后核对原单。',
};
export function steamLabError(error: unknown): string {
  return errors[error instanceof Error ? error.message : ''] ?? '请求暂未完成，请刷新状态；已发起的订单号会保留，请勿另外创建替代单。';
}
export const steamLabStages: Record<string, string> = {
  idle: '准备就绪', accepted: '等待本账号建单', awaiting_login: '等待 Steam 登录', account_mismatch: '登录账号不匹配',
  identity_check: '核验收款账号', wallet_page: '打开钱包页', login_check: '核验登录状态', amount_selection: '选择充值面额',
  payment_method: '选择付款方式', init_transaction: '创建 Steam 交易', price_validation: '核对金额与币种', agreement: '确认充值条款',
  payment_popup: '打开付款页', qr_detection: '读取付款二维码', query: '查询 Steam 结果', resume_query: '核对原交易',
  http_wallet: '核对钱包充值表单', http_wallet_submit: 'HTTP 提交钱包充值', http_checkout_page: '打开结账表单', http_init: 'HTTP 创建 Steam 交易', http_price: 'HTTP 核对金额与币种', http_qr: 'HTTP 读取收银台二维码原图', browser_qr: '浏览器读取付款二维码',
  resume_payment_page: '恢复原付款页', recovery_unknown: '创建结果待人工核对', storage_error: '记录保存异常', worker_error: '服务待检查', complete: 'Steam 已确认到账',
  test_ended: '测试已结束，可以创建新单',
};
const checkoutFailures: Record<string, string> = {
  query_unavailable: '本次未能取得 Steam 查单响应，到账结果尚未确认。请继续查询原单。',
  query_response_unconfirmed: 'Steam 查单响应未通过校验，到账结果尚未确认。请继续查询原单。',
  query_receipt_unconfirmed: 'Steam 返回的状态或收据与本单校验要求不符，请核对原交易。',
  query_requires_approval: 'Steam 返回此交易需要额外批准，请检查 Steam 原交易提示。',
  query_requires_authentication: 'Steam 返回此交易需要额外身份验证，请检查 Steam 原交易提示。',
  query_reported_failure: 'Steam 查单返回交易未成功，具体原因需核对 Steam 原交易。',
  resume_page_unavailable: '原交易仍待处理，但重新打开付款页未完成。原交易号已保留。',
  wallet_navigation_timeout: '服务器连接 Steam 钱包页超时，尚未提交交易。请先检查服务器到 Steam 的连接。',
  wallet_page_unavailable: '未能打开有效的 Steam 钱包页，尚未提交交易。请检查服务器连接和 Steam 页面响应。',
  wallet_content_timeout: '已连接 Steam，但钱包页内容未及时就绪，尚未提交交易。',
  transaction_conflict: 'Steam 返回的交易号已属于另一笔订单，本单已停止展示付款码，请核对原交易。',
  payment_method_unavailable: '当前 Steam 结账页没有提供所选支付方式，尚未提交交易。请检查账号地区与可用付款方式。',
  identity_navigation_timeout: '服务器连接 Steam 钱包页超时，未完成本次账号核验。',
  identity_page_unavailable: '服务器未能打开有效的 Steam 钱包页，未完成本次账号核验。',
  identity_check_unavailable: '未能及时读取 Steam 页面的登录身份，未完成本次账号核验。',
  worker_step_failed: '出码服务执行失败，请保留原订单检查服务；这不代表账号未绑定。',
  wallet_form_unavailable: '未读到支持的人民币充值表单，尚未提交 Steam 交易。',
  wallet_form_changed: '钱包充值表单字段与预期不一致，尚未提交 Steam 交易。',
  wallet_form_mismatch: '充值表单的币种或登录校验未通过，尚未提交 Steam 交易。',
  wallet_redirect_unconfirmed: '提交充值面额后，未收到预期的 Steam 结账页面；未继续提交交易。',
  checkout_page_unavailable: 'Steam 结账页面未能打开，未继续提交交易。',
  checkout_form_mismatch: '结账表单的账号地区、支付方式或字段校验未通过，未继续提交交易。',
  request_unconfirmed: 'Steam 请求未取得确定响应，请保留原单核对结果。',
  steam_pending_transaction: 'Steam 提示仍有待处理交易，请先核对 Steam 购买历史。',
  init_unconfirmed: 'Steam 建单接口未返回可确认的交易结果，请保留原单核对。',
  price_mismatch: '交易金额、币种或收银台跳转校验未通过，已停止打开付款码。',
  provider_entry_missing: '已取得交易结果，但没有读到可用的收银台入口。',
  browser_step_failed: '页面操作未完成，请核对下方停止步骤及原订单状态。',
};
export function steamLabFailure(order: SteamLabOrder): string | null {
  if (order.status === 'paid' || order.testEndedAt || order.status !== 'manual_review') return null;
  if (steamLabCanRetry(order)) return (checkoutFailures[order.failureCode ?? ''] ?? '本次在建单准备阶段停止。') + ' 已确认尚未提交交易，可点击“重试本单出码”，继续使用这笔订单。';
  return checkoutFailures[order.failureCode ?? ''] ?? (order.transid
    ? '这笔原交易需要核对，旧记录未记录具体失败原因。请点击“查询原单 / 刷新付款码”获取最新结果，不会创建替代交易。'
    : '本次结果需要核对，未记录具体失败原因。请核对原订单记录后处理。');
}

const qrIssues: Record<string, string> = {
  payment_page_missing: '交易已创建，但原收银台窗口尚未打开或已关闭。',
  payment_frame_unrecognized: '交易已创建，付款页尚未到达支持的收银台地址。',
  qr_element_missing: '收银台页面已到达，尚未找到可见的二维码候选图片或画布。',
  qr_validation_failed: '已读取收银台候选图片，但尚未识别出符合本单支付方式的有效付款码。',
  qr_scan_incomplete: '本轮二维码扫描达到时限，正在等待后续识别。',
};
export function steamLabQrIssue(order: SteamLabOrder): string | null {
  if (steamLabReleased(order) || order.qrReady || !order.transid) return null;
  return qrIssues[order.qrIssue ?? ''] ?? null;
}
